import axios, { type AxiosInstance } from 'axios';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';

const GATEWAY_BASE_URL = import.meta.env.VITE_GATEWAY_BASE_URL ?? '/api/gateway';
const AUTH_TOKEN_STORAGE_KEY = import.meta.env.VITE_AUTH_TOKEN_STORAGE_KEY ?? 'auth_token';

export const gatewayApi: AxiosInstance = axios.create({
    baseURL: GATEWAY_BASE_URL,
    withCredentials: true,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
    },
});

// Calls your Laravel app directly (session login/logout, etc.)
export const serverApi: AxiosInstance = axios.create({
    baseURL: '/api',
    withCredentials: true,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
    },
});

// Backwards-compatible export
export const api: AxiosInstance = gatewayApi;

// Intercepteur pour ajouter le token d'auth aux requêtes serverApi
// Note: les routes guest utilisent un cookie HTTP-only, pas ce token
serverApi.interceptors.request.use((config) => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers = config.headers ?? {};
    config.headers['Accept-Language'] = i18n.language;

    console.log('[ServerAPI Request]', {
        method: config.method?.toUpperCase(),
        baseURL: config.baseURL,
        url: config.url,
        fullUrl: `${config.baseURL}${config.url}`,
        hasAuth: !!config.headers?.Authorization,
    });

    return config;
});

gatewayApi.interceptors.request.use((config) => {
    // Ne pas injecter le token de l'utilisateur normal sur les routes guest (public/)
    // pour éviter que le proxy ne l'envoie à la place du cookie guest
    const isGuestRoute = config.url?.includes('/public/');
    if (!isGuestRoute) {
        const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        }
    }

    config.headers = config.headers ?? {};
    config.headers['Accept-Language'] = i18n.language;

    // DEBUG: Log la requête sortante
    console.log('[GatewayAPI Request]', {
        method: config.method?.toUpperCase(),
        baseURL: config.baseURL,
        url: config.url,
        fullUrl: `${config.baseURL}${config.url}`,
        hasAuth: !!config.headers?.Authorization,
    });

    return config;
});

// Intercepteur de réponse pour les erreurs globales
gatewayApi.interceptors.response.use(
    (response) => response,
    (error) => {
        // Erreurs réseau / timeout
        if (!error.response) {
            toast.error(i18n.t('common:errors.networkTitle'), {
                description: i18n.t('common:errors.networkDescription'),
            });
        }
        // Erreur 401 - Session expirée (ne pas afficher sur les routes de login)
        else if (error.response.status === 401) {
            const isAuthRoute = error.config?.url?.includes('/login') || error.config?.url?.includes('/register');
            if (!isAuthRoute) {
                toast.error(i18n.t('common:errors.sessionExpiredTitle'), {
                    id: 'session-expired',
                    description: i18n.t('common:errors.sessionExpiredDescription'),
                });
                // Déclencher un événement pour que auth-context puisse réagir
                window.dispatchEvent(new CustomEvent('auth:session-expired'));
            }
        }
        // Erreur 500+ - Erreur serveur
        else if (error.response.status >= 500) {
            toast.error(i18n.t('common:errors.serverTitle'), {
                description: i18n.t('common:errors.serverDescription'),
            });
        }

        return Promise.reject(error);
    },
);

export function setAuthToken(token: string | null) {
    if (!token) {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        return;
    }

    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
}
